/**
 * Timesteps Table Component
 *
 * Displays a table of all timesteps with their status and details.
 * Clicking a row navigates to the timestep detail page.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Table, Badge, Text, Card, Stack, ScrollArea } from "@mantine/core";
import type { TimestepSummary } from "../../types";

interface TimestepsTableProps {
  timesteps: TimestepSummary[];
  returnId: string;
}

export const TimestepsTable: React.FC<TimestepsTableProps> = ({
  timesteps,
  returnId,
}) => {
  const navigate = useNavigate();

  const handleRowClick = (timestepId: string) => {
    navigate(`/admin/returns/${returnId}/timesteps/${timestepId}`);
  };

  if (timesteps.length === 0) {
    return (
      <Card shadow="sm" padding="xl" radius="md" withBorder>
        <Stack align="center" gap="md" py="xl">
          <Text size="xl" fw={600} c="dimmed">
            No Timesteps Found
          </Text>
          <Text size="sm" c="dimmed">
            This return doesn't have any timestep data yet.
          </Text>
        </Stack>
      </Card>
    );
  }

  const rows = timesteps.map((timestep, index) => (
    <Table.Tr
      key={timestep.id}
      onClick={() => handleRowClick(timestep.id)}
      style={{ cursor: "pointer" }}
    >
      <Table.Td>
        <Text fw={600} c="blue" component="span">
          {index + 1}
        </Text>
        {timestep.type === "stop" && (
          <Badge color="red" variant="filled" size="xs" ml={6}>
            STOPPED
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="xs" ff="monospace" c="dimmed">
          {timestep.formattedTimestamp}
        </Text>
      </Table.Td>
      <Table.Td>
        {timestep.type === "stop" ? (
          <Text size="xs" c="dimmed" fs="italic">
            Session stopped
          </Text>
        ) : (
          <Text
            size="xs"
            style={{
              wordBreak: "break-all",
            }}
          >
            {timestep.url}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {timestep.inProgressFormName ? (
          <Badge color="blue" variant="light">
            {timestep.inProgressFormName}
          </Badge>
        ) : (
          <Text size="xs" c="dimmed" fs="italic">
            No form in progress
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            Missing: {timestep.missingCount}
          </Text>
          <Text size="xs" c="dimmed">
            In Progress: {timestep.inProgressCount}
          </Text>
          <Text size="xs" c="dimmed">
            Completed: {timestep.completedCount}
          </Text>
        </Stack>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <div>
          <Text size="xl" fw={600} mb="xs">
            Timestamp Details
          </Text>
          <Text size="sm" c="dimmed">
            Click on a timestamp to view detailed information.
          </Text>
        </div>

        <ScrollArea>
          <Table highlightOnHover striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: "80px" }}>
                  <Text size="xs" fw={600} tt="uppercase">
                    #
                  </Text>
                </Table.Th>
                <Table.Th style={{ width: "200px" }}>
                  <Text size="xs" fw={600} tt="uppercase">
                    Timestamp
                  </Text>
                </Table.Th>
                <Table.Th>
                  <Text size="xs" fw={600} tt="uppercase">
                    URL
                  </Text>
                </Table.Th>
                <Table.Th style={{ width: "200px" }}>
                  <Text size="xs" fw={600} tt="uppercase">
                    In Progress Form
                  </Text>
                </Table.Th>
                <Table.Th style={{ width: "150px" }}>
                  <Text size="xs" fw={600} tt="uppercase">
                    Form Status
                  </Text>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </ScrollArea>
      </Stack>
    </Card>
  );
};
